import React, { useState, useCallback, useEffect, useRef } from 'react';
import './App.css';
import { auth } from './data/auth';
import { syncAllFromServer as syncFromServer, pollForChanges } from './data/serverStore';

import { LOGO_BASE64 } from './data/logo';
import LoginScreen from './components/LoginScreen';
import AdminPanel from './components/AdminPanel';
import LogReport from './components/LogReport';
import SampleReceiving from './components/SampleReceiving';
import SampleSetup from './components/SampleSetup';
import EnterResults from './components/EnterResults';
import Reports from './components/Reports';
import History from './components/History';
import EmployeeTasks from './components/EmployeeTasks';
import Invoicing from './components/Invoicing';
import OperationsDashboard from './components/OperationsDashboard';
import FinanceDashboard from './components/FinanceDashboard';
import LabBoard from './components/LabBoard';
import SampleLoginQueue from './components/SampleLoginQueue';
import { approvals, db } from './data/store';
import Calculators from './components/Calculators';
import LabMethods from './components/LabMethods';
import HoursLog from './components/HoursLog';
import EmailAgentSetup from './components/EmailAgentSetup';
import Quoting from './components/Quoting';
import TeamChat, { getChatUnreadCount } from './components/TeamChat';
import CompletedSampleApprovals from './components/CompletedSampleApprovals';

const ALL_NAV = [
  { id:'ops',       label:'Operations', icon:'📊' },
  { id:'finance',   label:'Finance',    icon:'💹' },
  { id:'labboard',  label:'Lab Board',  icon:'🧪' },
  { id:'loginqueue',label:'Incoming Samples', icon:'📦' },
  { id:'chat',      label:'Team Chat',  icon:'💬' },
  { id:'log',       label:'Log Report', icon:'📋' },
  { id:'receive',   label:'Sample Receiving', icon:'📥' },
  { id:'setup',     label:'Sample Setup', icon:'🔬' },
  { id:'results',   label:'Enter Results', icon:'✅' },
  { id:'reports',   label:'Reports',    icon:'📤' },
  { id:'history',   label:'History',    icon:'📚' },
  { id:'my_tasks',  label:'My Tasks',   icon:'☑️' },
  { id:'employee_tasks', label:'Employee Tasks', icon:'📋' },
  { id:'calcs',     label:'Calculators',icon:'🔢' },
  { id:'lab_methods',label:'Lab Method', icon:'📚' },
  { id:'hours',     label:'Hours Log',   icon:'⏱' },
  { id:'invoice',   label:'Billing',    icon:'💰' },
  { id:'quotes',    label:'Quotes',     icon:'📋' },
  { id:'email',     label:'Email Setup', icon:'📧' },
  { id:'admin',     label:'Admin',      icon:'⚙️' },
];

function Toast({ toasts }) {
  const COLORS = { success:'#1a6b42', error:'#b83232', info:'#1a7dc4', warn:'#a06000' };
  return (
    <div style={{position:'fixed',bottom:20,right:20,zIndex:9999,
      display:'flex',flexDirection:'column',gap:8}}>
      {toasts.map(t=>(
        <div key={t.id} style={{background:COLORS[t.type]||COLORS.info,color:'white',
          padding:'10px 18px',borderRadius:6,fontSize:13,fontWeight:600,
          boxShadow:'0 4px 16px rgba(0,0,0,0.25)',maxWidth:340,
          animation:'slideIn 0.2s ease'}}>
          {t.msg}
        </div>
      ))}
      <style>{`@keyframes slideIn{from{transform:translateX(100%);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>
    </div>
  );
}

export default function App() {
  const [session,        setSession]        = useState(null);
  const [syncing,        setSyncing]        = useState(true); // wait for server sync before showing login
  const [appKey,         setAppKey]         = useState(0); // increment to force all screens to remount
  const [screen,         setScreen]         = useState('ops');
  const [currentSample,  setCurrentSample]  = useState(null);
  const [reportKey,      setReportKey]      = useState(0);
  const [reportRefresh,  setReportRefresh]  = useState(0);
  const [toasts,         setToasts]         = useState([]);
  const [clock,          setClock]          = useState('');
  const [chatUnread,     setChatUnread]     = useState(0);
  // Long-running scanner/OCR workflows must not be destroyed by the normal
  // five-second background sync.  SampleReceiving announces when one of
  // those workflows is active; a ref is used so the polling closure always
  // sees the current value without restarting the interval.
  const workflowBusyRef = useRef(false);

  useEffect(() => {
    const onWorkflowBusy = (e) => {
      workflowBusyRef.current = Boolean(e.detail);
    };
    window.addEventListener('lims-workflow-busy', onWorkflowBusy);
    return () => window.removeEventListener('lims-workflow-busy', onWorkflowBusy);
  }, []);

  useEffect(()=>{
    // Sync all data from server on startup
    // Initial sync from server
    const doSync = async () => {
      await syncFromServer().catch(()=>{});
      const freshSession = await auth.getSession();
      setSession(freshSession); // null is a valid, meaningful result here — not logged in
      if (freshSession && freshSession.role !== 'admin') setScreen('my_tasks');
    };

    doSync()
      .finally(() => { setSyncing(false); setAppKey(k => k + 1); });
    // Fallback: proceed after 3s even if server is slow
    setTimeout(() => { setSyncing(false); setAppKey(k => k + 1); }, 3000);

    // Poll server every 5 seconds for changes — remount only if data changed.
    // pollInFlight guards against a slow/stuck poll (e.g. mid network
    // change) getting a second, third, fourth... poll piled on top of it
    // every 5s — that pile-up, each one opening its own requests and each
    // successful one forcing a full remount (which itself reopens
    // TeamChat's WebSocket connection), is what turned a brief network
    // hiccup into a tab that never recovered.
    let pollInFlight = false;
    const syncInterval = setInterval(async () => {
      if (pollInFlight) return;
      pollInFlight = true;
      try {
        const changed = await pollForChanges();
        if (changed && !workflowBusyRef.current) setAppKey(k => k + 1);
      } finally {
        pollInFlight = false;
      }
    }, 5000);
    return () => { clearInterval(syncInterval); };
  },[]);

  // Clock display — separate effect (previously entangled with the sync
  // effect above behind an early return, which silently prevented this from
  // ever running at all). Browser-based auto-backup has been intentionally
  // removed: backups now run server-side only (see server/index.js), so a
  // compromised or deleted browser/localStorage can never be the only copy.
  useEffect(()=>{
    const update = ()=>setClock(new Date().toLocaleTimeString());
    update();
    const t = setInterval(update, 1000);
    return ()=>clearInterval(t);
  },[]);

  // Poll unread chat count for the nav badge — separate from the main sync
  // effect above so it doesn't get tangled up with that one's early return.
  useEffect(() => {
    if (!session) return;
    let stopped = false;
    let checkInFlight = false;
    const check = () => {
      if (checkInFlight) return;
      checkInFlight = true;
      getChatUnreadCount(session)
        .then(n => { if (!stopped) setChatUnread(n); })
        .finally(() => { checkInFlight = false; });
    };
    check();
    const interval = setInterval(check, 8000);
    return () => { stopped = true; clearInterval(interval); };
  }, [session]);

  const toast = useCallback((type, msg)=>{
    const id = Date.now()+Math.random();
    setToasts(prev=>[...prev,{id,type,msg}]);
    setTimeout(()=>setToasts(prev=>prev.filter(t=>t.id!==id)), 3200);
  },[]);

  useEffect(() => {
    const onConflict = async event => {
      const detail = event.detail || {};
      toast(
        'warn',
        `${detail.message || 'This record was changed by another user.'} The latest server version has been loaded; review it before saving again.`
      );
      await syncFromServer().catch(() => {});
      setAppKey(key => key + 1);
    };
    window.addEventListener('lims-data-conflict', onConflict);
    return () => window.removeEventListener('lims-data-conflict', onConflict);
  }, [toast]);

  const canAccessScreen = useCallback(id => auth.canAccess(id), []);

  const navigate = useCallback((id)=>{
    if (!canAccessScreen(id) && id !== 'admin' && id !== 'chat' && id !== 'my_tasks' && id !== 'lab_methods') {
      toast('error','You do not have access to this screen');
      return;
    }
    // When navigating to Enter Results, refresh currentSample from DB so params are up to date
    if (id === 'results' && currentSample) {
      const fresh = db.getSamples().find(s => s.id === currentSample.id);
      if (fresh) setCurrentSample(fresh);
    }
    // Always remount Reports with fresh data
    if (id === 'reports') {
      setReportKey(k => k + 1);
    }
    // Also refresh when leaving Enter Results
    if (screen === 'results' && id !== 'results') {
      setReportKey(k => k + 1);
    }
    setScreen(id);
  },[currentSample, canAccessScreen]);

  const selectSample = useCallback((s)=>setCurrentSample(s),[]);

  const handleLogin = async (username, password) => {
    const result = await auth.login(username, password);
    if (result.error) return result; // return error to LoginScreen
    setSession(result.session);
    if (result.session.role !== 'admin') setScreen('my_tasks');
    else {
      const first = ALL_NAV.find(n=>result.session.screens.includes(n.id));
      if (first) setScreen(first.id);
    }
    return result;
  };

  const handleLogout = async () => {
    await auth.logout();
    setSession(null);
    setCurrentSample(null);
  };

  const forceRefreshReports = useCallback(() => setReportRefresh(r => r + 1), []);

  // Show brief loading while syncing from server
  if (syncing) {
    return (
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',
        height:'100vh',background:'#1a2744',flexDirection:'column',gap:16}}>
        <div style={{color:'white',fontSize:20,fontWeight:700}}>Sterling LIMS</div>
        <div style={{color:'#7ec8e3',fontSize:13}}>⏳ Loading...</div>
      </div>
    );
  }

  // Not logged in → show login screen
  if (!session) {
    return <LoginScreen onLogin={handleLogin}/>;
  }

  const sharedProps = {
    onNavigate: navigate,
    onSelectSample: selectSample,
    currentSample,
    toast,
    session,
  };

  // Visible nav items — only screens user has access to, except Chat which
  // every logged-in staff member can always reach regardless of role.
  const visibleNav = ALL_NAV.filter(n => (canAccessScreen(n.id) || n.id === 'chat' || n.id === 'my_tasks' || n.id === 'lab_methods') &&
    (n.id !== 'hours' || session.role === 'admin'));

  // If current screen not allowed, go to first allowed
  const activeScreen = (canAccessScreen(screen) || screen === 'chat' || screen === 'my_tasks' || screen === 'lab_methods') ? screen : (visibleNav[0]?.id || 'log');

  return (
    <div className="app">
      {/* Topbar */}
      <div className="topbar">
        <img src={LOGO_BASE64} alt="Sterling Analytical"
          style={{height:30,width:'auto',filter:'brightness(0) invert(1)',marginRight:4}}/>
        <span className="topbar-sub" style={{fontSize:11,color:'#7788aa'}}>LIMS v1.0</span>
        <div className="topbar-spacer"/>
        {currentSample && (
          <div className="topbar-meta">
            <span>🔬 Active: <strong style={{color:'#7ec8e3'}}>{currentSample.id}</strong> — {currentSample.source}</span>
          </div>
        )}
        <div className="topbar-meta" style={{marginLeft:16,display:'flex',
          alignItems:'center',gap:12}}>
          {/* User info */}
          <div style={{display:'flex',alignItems:'center',gap:6}}>
            <div style={{width:26,height:26,borderRadius:'50%',background:'#2a4080',
              display:'flex',alignItems:'center',justifyContent:'center',
              fontSize:12,fontWeight:700,color:'#7ec8e3'}}>
              {session.displayName?.[0]?.toUpperCase()||'?'}
            </div>
            <div>
              <div style={{fontSize:11,fontWeight:600,color:'white',lineHeight:1.1}}>
                {session.displayName}
              </div>
              <div style={{fontSize:9,color:'#7788aa',textTransform:'capitalize'}}>
                {session.role}
              </div>
            </div>
          </div>
          {/* Admin shortcut */}
          {session.screens.includes('admin') && (
            <button onClick={()=>navigate('admin')}
              style={{background:'none',border:'1px solid #2a4080',borderRadius:4,
                color:'#7788aa',cursor:'pointer',fontSize:11,padding:'3px 8px'}}>
              ⚙️
            </button>
          )}
          {/* Logout */}
          <button onClick={handleLogout}
            style={{background:'none',border:'1px solid #b83232',borderRadius:4,
              color:'#ff8888',cursor:'pointer',fontSize:11,padding:'3px 8px',
              fontWeight:600}}>
            Sign Out
          </button>
          <span style={{fontSize:11,color:'#7788aa'}}>⏰ {clock}</span>
        </div>
      </div>

      {/* Navbar — only show accessible screens */}
      <div className="navbar">
        {visibleNav.map(item=>{
          const pendingCount = item.id==='admin' ? (approvals?.getCount?.() || 0)
            : item.id==='chat' ? chatUnread : 0;
          return (
            <button key={item.id}
              className={`nav-btn ${activeScreen===item.id?'active':''}`}
              onClick={()=>navigate(item.id)}>
              {item.icon} {item.label}
              {pendingCount>0 && (
                <span style={{marginLeft:5,background:'#b83232',color:'white',
                  borderRadius:10,padding:'1px 6px',fontSize:10,fontWeight:700}}>
                  {pendingCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Main — key forces full remount after server sync so all screens read fresh data */}
      <div className="main" key={appKey}>
        <div className={`screen ${activeScreen==='ops'?'active':''}`}>
          <OperationsDashboard {...sharedProps}/>
        </div>
        <div className={`screen ${activeScreen==='finance'?'active':''}`}>
          <FinanceDashboard {...sharedProps}/>
        </div>
        <div className={`screen ${activeScreen==='labboard'?'active':''}`}>
          <LabBoard {...sharedProps}/>
        </div>
        <div className={`screen ${activeScreen==='loginqueue'?'active':''}`}>
          <SampleLoginQueue {...sharedProps}/>
        </div>
        <div className={`screen ${activeScreen==='chat'?'active':''}`}>
          <TeamChat session={session} toast={toast}/>
        </div>
        <div className={`screen ${activeScreen==='log'?'active':''}`}>
          <LogReport {...sharedProps}/>
        </div>
        <div className={`screen ${activeScreen==='receive'?'active':''}`}>
          <SampleReceiving {...sharedProps} isActive={activeScreen==='receive'}/>
        </div>
        <div className={`screen ${activeScreen==='setup'?'active':''}`}>
          <SampleSetup {...sharedProps} isActive={activeScreen==='setup'}/>
        </div>
        <div className={`screen ${activeScreen==='results'?'active':''}`}>
          <EnterResults {...sharedProps}/>
        </div>
        <div className={`screen ${activeScreen==='reports'?'active':''}`}>
          <Reports key={`${reportKey}-${reportRefresh}`} {...sharedProps}
            isActive={activeScreen==='reports'}/>
        </div>
        <div className={`screen ${activeScreen==='history'?'active':''}`}>
          <History {...sharedProps}/>
        </div>
        <div className={`screen ${activeScreen==='my_tasks'?'active':''}`}>
          <EmployeeTasks {...sharedProps} mode="mine"/>
        </div>
        <div className={`screen ${activeScreen==='employee_tasks'?'active':''}`}>
          <EmployeeTasks {...sharedProps} mode="admin"/>
        </div>
        <div className={`screen ${activeScreen==='invoice'?'active':''}`}>
          <Invoicing {...sharedProps}/>
        </div>
        <div className={`screen ${activeScreen==='calcs'?'active':''}`}>
          <Calculators/>
        </div>

        <div className={`screen ${activeScreen==='lab_methods'?'active':''}`}>
          <LabMethods session={session} toast={toast}/>
        </div>

        <div className={`screen ${activeScreen==='hours'?'active':''}`}>
          <HoursLog session={session} toast={toast}/>
        </div>

        <div className={`screen ${activeScreen==='quotes'?'active':''}`}>
          <Quoting toast={toast}/>
        </div>

        <div className={`screen ${activeScreen==='email'?'active':''}`}>
          <EmailAgentSetup toast={toast}/>
        </div>

        <div className={`screen ${activeScreen==='admin'?'active':''}`}>
          {session.screens.includes('admin')
            ? <AdminPanel session={session} toast={toast}/>
            : <div className="content"><div className="notice notice-warn">
                Access denied — Admin only
              </div></div>
          }
        </div>
      </div>

      <CompletedSampleApprovals session={session} toast={toast}/>
      <Toast toasts={toasts}/>
    </div>
  );
}
